import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { datadogLogs } from '@datadog/browser-logs';
import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, IconButton, Modal, Typography } from '@mui/material';

import asyncService from '../../../../../../../../datahub/asyncService';
import WaeButton, { BUTTON_TYPE, BUTTON_VARIANT } from '../../../../../../../components/Button';
import FileUpload from '../../../../../../../components/FileUpload';
import { SelectDropdownFilter } from '../../../../../../../components/SelectDropdownFilter/SelectDropdownFilter';
import selectUser from '../../../../../../../store/selectors/appSelector';
import { BLACK, FEED_BACK, PRIMARY_PURPLE, RED } from '../../../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../../../theme/fontConstants';
import {
  PRESIGN_UPLOAD_ROUTE,
  UPLOAD_ACCEPT,
  UPLOAD_DOCUMENT_TYPE_OPTIONS,
  UPLOAD_MAX_SIZE_MB,
  UPLOAD_STATUS,
} from '../constants';

const modalRootSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '600px' },
  maxHeight: '80vh',
  bgcolor: BLACK[0],
  borderRadius: '12px',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};

const headerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleSx = {
  fontSize: '20px',
  fontFamily: PRIMARY_FONT[700],
  color: BLACK[100],
  lineHeight: '24px',
};

const closeIconButtonSx = {
  color: BLACK[60],
  p: 0.5,
};

const bodySx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2.5,
};

const fieldLabelSx = {
  fontFamily: PRIMARY_FONT[500],
  fontSize: '13px',
  color: BLACK[80],
  mb: 0.75,
};

const documentTypeDropdownWrapperSx = {
  width: '100%',
  height: '40px',
  minHeight: '40px',
  border: `1px solid ${BLACK[30]}`,
  borderRadius: '8px',
  '&:hover': {
    borderColor: PRIMARY_PURPLE[50],
  },
  '&:focus-within': {
    borderColor: PRIMARY_PURPLE[60],
  },
  '& .MuiOutlinedInput-root': {
    background: 'transparent',
    padding: '0 8px !important',
    fontFamily: PRIMARY_FONT[400],
    fontSize: '14px',
    color: BLACK[100],
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '0px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: BLACK[40],
    fontFamily: PRIMARY_FONT[400],
    fontSize: '14px',
    opacity: 1,
  },
  '& .MuiInputBase-inputAdornedEnd': {
    background: 'transparent',
    color: BLACK[100],
    height: 'auto',
    borderRadius: 0,
    fontFamily: PRIMARY_FONT[400],
    fontSize: '14px',
  },
};

const documentTypeDropdownTextboxSx = {
  '& .MuiInputBase-input': {
    fontFamily: PRIMARY_FONT[400],
    fontSize: '14px',
    color: BLACK[100],
  },
};

const documentTypeDropdownPopperSx = {
  zIndex: (dropdownTheme) => dropdownTheme.zIndex.modal + 1,
};

const errorTextSx = {
  fontFamily: PRIMARY_FONT[400],
  fontSize: '13px',
  color: RED[70],
};

const statusStateSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  py: 3,
  gap: 1,
};

const footerSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 1.5,
  borderTop: `1px solid ${BLACK[20]}`,
  pt: 2,
};

const cancelButtonSx = {
  color: PRIMARY_PURPLE[70],
  background: 'transparent',
  border: `1px solid ${PRIMARY_PURPLE[70]}`,
  boxShadow: 'none',
  height: '36px',
  '&:hover': {
    backgroundColor: PRIMARY_PURPLE[10],
    boxShadow: 'none',
  },
};

const primaryButtonSx = {
  height: '36px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: PRIMARY_PURPLE[60],
    boxShadow: 'none',
  },
  '&:disabled': {
    backgroundColor: BLACK[20],
    color: BLACK[50],
    cursor: 'default',
  },
};

const uploadAnotherButtonSx = {
  minWidth: 0,
  height: 'auto',
  px: 0,
  py: 0,
  boxShadow: 'none',
  textTransform: 'none',
  fontFamily: PRIMARY_FONT[500],
  fontSize: '13px',
  color: PRIMARY_PURPLE[70],
  '&:hover': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
};

const DocumentUploadModal = ({ open, onClose, userId, onUploadSuccess }) => {
  const dispatch = useDispatch();
  const sessionUser = useSelector(selectUser);
  const { token, refreshToken } = sessionUser || {};
  const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.IDLE);
  const [pendingFile, setPendingFile] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const isUploadingRef = useRef(false);
  const isOpenRef = useRef(false);
  const uploadAttemptIdRef = useRef(0);

  useEffect(() => {
    isOpenRef.current = open;
    uploadAttemptIdRef.current += 1;
    isUploadingRef.current = false;

    // Reset on both open and close so no success/error state from a previous
    // session lingers while the modal is closed and briefly flashes on reopen.
    setUploadStatus(UPLOAD_STATUS.IDLE);
    setPendingFile(null);
    setSelectedDocumentType('');
  }, [open]);

  const isCurrentUploadAttempt = useCallback(
    (attemptId) => isOpenRef.current && uploadAttemptIdRef.current === attemptId,
    []
  );

  const uploadFileToS3 = useCallback(
    (file, intent) => {
      if (!file || !intent || isUploadingRef.current) return;

      if (!userId) {
        datadogLogs.logger.error('DocumentUploadModal: upload attempted without userId', {
          fileName: file?.name,
        });
        setUploadStatus(UPLOAD_STATUS.ERROR);
        return;
      }

      isUploadingRef.current = true;
      uploadAttemptIdRef.current += 1;
      const uploadAttemptId = uploadAttemptIdRef.current;
      setUploadStatus(UPLOAD_STATUS.UPLOADING);

      asyncService({
        httpMethod: 'POST',
        route: PRESIGN_UPLOAD_ROUTE,
        data: {
          entityType: 'User',
          entityId: userId,
          fileName: file.name,
          fileType: file.type,
          intent,
        },
        onSuccess: (response) => {
          if (!isCurrentUploadAttempt(uploadAttemptId)) return;

          // response also includes documentId — use it for presigned download when ready
          const presignedUrl = response?.data?.presignedUrl || '';
          if (!presignedUrl) {
            datadogLogs.logger.error('DocumentUploadModal: presigned URL missing from response', {
              userId,
              fileName: file.name,
            });
            isUploadingRef.current = false;
            setUploadStatus(UPLOAD_STATUS.ERROR);
            return;
          }

          // Direct PUT to S3 via presigned URL (requires CORS on the bucket)
          fetch(presignedUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
          })
            .then((res) => {
              if (!isCurrentUploadAttempt(uploadAttemptId)) return;
              if (!res.ok) throw new Error(`S3 PUT failed with status ${res.status}`);
              isUploadingRef.current = false;
              setUploadStatus(UPLOAD_STATUS.SUCCESS);

              if (onUploadSuccess) {
                onUploadSuccess();
              }
            })
            .catch((err) => {
              if (!isCurrentUploadAttempt(uploadAttemptId)) return;
              datadogLogs.logger.error('DocumentUploadModal: S3 PUT failed', {
                userId,
                fileName: file.name,
                message: err?.message,
              });
              isUploadingRef.current = false;
              setUploadStatus(UPLOAD_STATUS.ERROR);
            });
        },
        onError: (err) => {
          if (!isCurrentUploadAttempt(uploadAttemptId)) return;
          datadogLogs.logger.error('DocumentUploadModal: presigned URL request failed', {
            userId,
            fileName: file.name,
            status: err?.response?.status,
            message: err?.message,
          });
          isUploadingRef.current = false;
          setUploadStatus(UPLOAD_STATUS.ERROR);
        },
        dispatch,
        token,
        refreshToken,
      });
    },
    [userId, dispatch, token, refreshToken, onUploadSuccess, isCurrentUploadAttempt]
  );

  const handleFileStaged = useCallback((file) => {
    setPendingFile(file);
    setUploadStatus(UPLOAD_STATUS.IDLE);
  }, []);

  const handleFileReset = useCallback(() => {
    setPendingFile(null);
    setUploadStatus(UPLOAD_STATUS.IDLE);
  }, []);

  const handleFileRejected = useCallback(() => {
    // Keep parent state aligned when the dropzone rejects a replacement file.
    setPendingFile(null);
    setUploadStatus(UPLOAD_STATUS.IDLE);
  }, []);

  const handleSubmitUpload = useCallback(() => {
    uploadFileToS3(pendingFile, selectedDocumentType);
  }, [uploadFileToS3, pendingFile, selectedDocumentType]);

  const handleUploadAnother = useCallback(() => {
    isUploadingRef.current = false;
    setUploadStatus(UPLOAD_STATUS.IDLE);
    setPendingFile(null);
    setSelectedDocumentType('');
  }, []);

  const canSubmit =
    !!selectedDocumentType && !!pendingFile && uploadStatus !== UPLOAD_STATUS.UPLOADING;
  const isUploading = uploadStatus === UPLOAD_STATUS.UPLOADING;
  const documentTypeOptions = useMemo(
    () =>
      UPLOAD_DOCUMENT_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        name: LanguageConverterV2(option.labelKey || option.value),
      })),
    []
  );
  const selectedDocumentTypeOption = useMemo(
    () => documentTypeOptions.find((option) => option.value === selectedDocumentType) || null,
    [documentTypeOptions, selectedDocumentType]
  );
  const handleDocumentTypeChange = useCallback((nextValue) => {
    setSelectedDocumentType(nextValue || '');
  }, []);

  const handleRequestClose = useCallback(
    (event, reason) => {
      if (isUploading) return;
      onClose(event, reason);
    },
    [isUploading, onClose]
  );

  const renderBody = () => {
    if (uploadStatus === UPLOAD_STATUS.SUCCESS) {
      return (
        <Box sx={statusStateSx}>
          <Typography
            sx={{
              fontFamily: PRIMARY_FONT[500],
              fontSize: '14px',
              color: FEED_BACK.GREEN_80,
            }}
          >
            {LanguageConverterV2('userDetail.employeeDocumentation.upload.success')}
          </Typography>
          <WaeButton
            type={BUTTON_TYPE.BUTTON}
            variant={BUTTON_VARIANT.DEFAULT}
            actionColor="transparent"
            hoverColor="transparent"
            onClick={handleUploadAnother}
            sx={uploadAnotherButtonSx}
          >
            {LanguageConverterV2('userDetail.employeeDocumentation.upload.another')}
          </WaeButton>
        </Box>
      );
    }

    return (
      <Box sx={bodySx}>
        {isUploading && (
          <Box sx={statusStateSx}>
            <CircularProgress size={32} sx={{ color: PRIMARY_PURPLE[60] }} />
            <Typography
              sx={{
                fontFamily: PRIMARY_FONT[400],
                fontSize: '14px',
                color: BLACK[60],
              }}
            >
              {LanguageConverterV2('userDetail.employeeDocumentation.upload.uploading')}
            </Typography>
          </Box>
        )}

        <Box>
          <Typography sx={fieldLabelSx}>
            {LanguageConverterV2('userDetail.employeeDocumentation.upload.documentType.label')}
          </Typography>
          <SelectDropdownFilter
            disabled={isUploading}
            placeholder={LanguageConverterV2(
              'userDetail.employeeDocumentation.upload.documentType.placeholder'
            )}
            options={documentTypeOptions}
            getOptionLabel={(option) => option.name}
            onValueChange={handleDocumentTypeChange}
            initialValue={selectedDocumentTypeOption}
            showOptionCheckbox={false}
            wrapperSx={documentTypeDropdownWrapperSx}
            textboxSx={documentTypeDropdownTextboxSx}
            popperSx={documentTypeDropdownPopperSx}
          />
        </Box>

        <FileUpload
          accept={UPLOAD_ACCEPT}
          maxSize={UPLOAD_MAX_SIZE_MB}
          maxFiles={1}
          onFileAccepted={handleFileStaged}
          onFileRejected={handleFileRejected}
          onReset={handleFileReset}
          disabled={isUploading}
        />

        {uploadStatus === UPLOAD_STATUS.ERROR && (
          <Typography sx={errorTextSx}>
            {LanguageConverterV2('userDetail.employeeDocumentation.upload.error')}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleRequestClose}
      disableEnforceFocus
      aria-labelledby="document-upload-modal-title"
      aria-describedby="document-upload-modal-description"
    >
      <Box sx={modalRootSx}>
        <Box sx={headerSx}>
          <Typography id="document-upload-modal-title" sx={titleSx}>
            {LanguageConverterV2('userDetail.employeeDocumentation.upload.title')}
          </Typography>
          <IconButton
            onClick={handleRequestClose}
            disabled={isUploading}
            sx={closeIconButtonSx}
            aria-label={LanguageConverterV2('button.close')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box id="document-upload-modal-description">{renderBody()}</Box>

        {uploadStatus !== UPLOAD_STATUS.SUCCESS && (
          <Box sx={footerSx}>
            <WaeButton
              type={BUTTON_TYPE.BUTTON}
              variant={BUTTON_VARIANT.DEFAULT}
              actionColor="transparent"
              hoverColor={PRIMARY_PURPLE[10]}
              borderColor={PRIMARY_PURPLE[70]}
              onClick={handleRequestClose}
              disabled={isUploading}
              sx={cancelButtonSx}
            >
              {LanguageConverterV2('button.cancel')}
            </WaeButton>
            <WaeButton
              type={BUTTON_TYPE.BUTTON}
              variant={BUTTON_VARIANT.DEFAULT}
              actionColor={PRIMARY_PURPLE[70]}
              hoverColor={PRIMARY_PURPLE[60]}
              onClick={handleSubmitUpload}
              disabled={!canSubmit}
              sx={primaryButtonSx}
            >
              {LanguageConverterV2('userDetail.employeeDocumentation.upload.submit')}
            </WaeButton>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

DocumentUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  onUploadSuccess: PropTypes.func,
};

DocumentUploadModal.defaultProps = {
  onUploadSuccess: () => {},
};

export default DocumentUploadModal;
